import { StrictMode, useContext } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

import './styles.css'
import reportWebVitals from './reportWebVitals.ts'
import { AuthContext, AuthProvider } from './contexts/AuthContext.tsx'
import Loading from './components/loading-ui/LoadingFullPage.tsx'
import { ThemeProvider } from './contexts/ThemeProvider.tsx'
import RootErrorComponent from './components/RootErrorComponent.tsx'
import * as Sentry from '@sentry/react'
import { Buffer } from 'buffer'
;(globalThis as any).Buffer = (globalThis as any).Buffer || Buffer

const queryClient = new QueryClient()

// Save native history methods BEFORE TanStack Router patches them.
// Dioxus WASM components call replaceState during init, but TanStack Router
// intercepts patched replaceState as navigation → re-triggers beforeLoad.
const _nativeReplaceState = history.replaceState.bind(history)
const _nativePushState = history.pushState.bind(history)

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    authState: null,
    TENANT_ID: null,
    isOrgAdmin: false,
    isOrgEditor: false,
    isSysAdmin: false,
    hasAnyEditorAccess: false,
    permissionExpression: null,
    availableTenants: [],
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 1000 * 60 * 5, // 5 minutes
  defaultErrorComponent: ({ error }) => <RootErrorComponent error={error} />,
})

// Patch AFTER createRouter: TanStack Router has now wrapped pushState/replaceState
// with its onPushPop listener. We re-wrap to suppress Dioxus's replaceState calls
// (which use the current URL for internal state) while letting TanStack's own calls through.
{
  const tsrReplace = history.replaceState // TanStack Router's wrapped version
  const tsrPush = history.pushState
  history.replaceState = function (
    data: any,
    unused: string,
    url?: string | URL | null,
  ) {
    if (!url) return _nativeReplaceState.call(this, data, unused)
    const u = String(url)
    const cur = location.pathname + location.search + location.hash
    // Dioxus passes current URL for scroll state — bypass TanStack Router's listener
    if (u === cur || u === location.href || u === location.pathname) {
      return _nativeReplaceState.call(this, data, unused, url)
    }
    return tsrReplace.call(this, data, unused, url)
  }
  history.pushState = function (
    data: any,
    unused: string,
    url?: string | URL | null,
  ) {
    if (!url) return _nativePushState.call(this, data, unused)
    const u = String(url)
    const cur = location.pathname + location.search + location.hash
    if (u === cur || u === location.href || u === location.pathname) {
      return _nativePushState.call(this, data, unused, url)
    }
    return tsrPush.call(this, data, unused, url)
  }
}

Sentry.init({
  dsn: 'https://7f2cb234e02fe2dc15f1e7b2fd3337d3@o4509472431407104.ingest.us.sentry.io/4509477759221760',
  tunnel: '/sentry',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment: import.meta.env.MODE,
  enabled: !(
    typeof window !== 'undefined' &&
    window.location?.hostname?.includes('localhost')
  ),
  tracesSampleRate: 1.0,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const App = () => {
  const { loading } = useContext(AuthContext)

  // Do NOT pass context={{ authState }} to RouterProvider — it calls
  // router.update() on every re-render, which re-triggers beforeLoad.
  // Instead, beforeLoad reads auth.currentUser directly from Firebase.
  return loading ? <Loading /> : <RouterProvider router={router} />
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
